import React, { FC } from 'react';
import styled from 'styled-components';
import { PageBlocksListSkeleton } from 'src/components/widgets/pageBlocksList/pageBlocksList';
import { SkeletonBlock, SkeletonPageHeader, SkeletonPageShell, SkeletonRow, SkeletonStack, SkeletonSurface } from 'src/components/widgets/skeleton';

const SectionSkeleton = styled(SkeletonSurface)<{ $margin?: string }>`
    border-color: #eef1f3;
    margin: ${(props) => props.$margin ?? '0'};
`;

const SectionRow = styled.div`
    display: flex;
    gap: 30px;
    width: 100%;
    min-width: 0;

    @media (max-width: 900px) {
        flex-direction: column;
    }
`;

const SectionLeft = styled(SkeletonStack)`
    flex: 0 0 20%;
    min-width: 0;
`;

const SectionRight = styled(SkeletonStack)`
    flex: 1 1 auto;
    min-width: 0;
`;

const ActionRowSkeleton = styled(SkeletonRow)`
    justify-content: space-between;
    padding: 16px 20px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
`;

const Divider = styled.div`
    border-top: 1px solid #e8edf0;
    margin: 24px 0 0;
`;

export const UserInterfaceSettingsPageSkeleton: FC = () => (
    <SkeletonPageShell aria-hidden='true'>
        <SkeletonPageHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(280px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(540px, 100%)' $height='13px' />
            </SkeletonStack>
        </SkeletonPageHeader>
        <SectionSkeleton $padding='20px 24px' $radius='16px' $margin='0 0 20px'>
            <SectionRow>
                <SectionLeft $gap='10px'>
                    <SkeletonBlock $width='90px' $height='18px' />
                    <SkeletonStack $gap='8px'>
                        <SkeletonBlock $width='100%' $height='12px' />
                        <SkeletonBlock $width='92%' $height='12px' />
                        <SkeletonBlock $width='78%' $height='12px' />
                    </SkeletonStack>
                    <SkeletonBlock $width='78px' $height='13px' />
                </SectionLeft>
                <SectionRight $gap='16px'>
                    <ActionRowSkeleton>
                        <SkeletonBlock $width='142px' $height='16px' />
                        <SkeletonBlock $width='14px' $height='18px' />
                    </ActionRowSkeleton>
                    <SkeletonBlock $width='min(260px, 100%)' $height='14px' />
                    <Divider />
                    <SkeletonRow $justify='space-between'>
                        <SkeletonBlock $width='160px' $height='16px' />
                        <SkeletonBlock $width='72px' $height='14px' />
                    </SkeletonRow>
                    {Array.from({ length: 2 }).map((_, index) => (
                        <ActionRowSkeleton key={index}>
                            <SkeletonStack $gap='6px'>
                                <SkeletonBlock $width={index === 0 ? '132px' : '116px'} $height='16px' />
                                <SkeletonBlock $width='84px' $height='12px' />
                            </SkeletonStack>
                            <SkeletonRow $gap='12px'>
                                <SkeletonBlock $width='20px' $height='20px' $radius='4px' />
                                <SkeletonBlock $width='20px' $height='20px' $radius='4px' />
                            </SkeletonRow>
                        </ActionRowSkeleton>
                    ))}
                </SectionRight>
            </SectionRow>
        </SectionSkeleton>
        <SkeletonBlock $height='1px' $radius='0' $margin='0 0 10px' />
        <SectionSkeleton $padding='20px 24px' $radius='16px'>
            <SectionRow>
                <SectionLeft $gap='10px'>
                    <SkeletonBlock $width='150px' $height='18px' />
                    <SkeletonBlock $width='min(220px, 100%)' $height='12px' />
                </SectionLeft>
                <SectionRight>
                    <PageBlocksListSkeleton count={2} columns={3} />
                </SectionRight>
            </SectionRow>
        </SectionSkeleton>
    </SkeletonPageShell>
);
